import { Component, EventEmitter, forwardRef, Input, OnInit, Output } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

export interface AreaGroup {
  name: string;
  key: string;
  prefectures: string[];
}

export const AREA_GROUPS: AreaGroup[] = [
  {
    name: '北海道・東北',
    key: 'hokkaido-tohoku',
    prefectures: ['北海道', '青森県', '岩手県', '秋田県', '山形県', '宮城県', '福島県'],
  },
  {
    name: '関東',
    key: 'kanto',
    prefectures: ['東京都', '神奈川県', '千葉県', '埼玉県', '栃木県', '群馬県', '茨城県'],
  },
  {
    name: '北陸・甲信越',
    key: 'hokuriku-koshinetsu',
    prefectures: ['新潟県', '富山県', '石川県', '福井県', '長野県', '山梨県'],
  },
  {
    name: '東海',
    key: 'tokai',
    prefectures: ['愛知県', '岐阜県', '三重県', '静岡県'],
  },
  {
    name: '近畿',
    key: 'kinki',
    prefectures: ['大阪府', '京都府', '兵庫県', '奈良県', '滋賀県', '和歌山県'],
  },
  {
    name: '中国',
    key: 'chugoku',
    prefectures: ['岡山県', '広島県', '鳥取県', '島根県', '山口県'],
  },
  {
    name: '四国',
    key: 'shikoku',
    prefectures: ['香川県', '徳島県', '愛媛県', '高知県'],
  },
  {
    name: '九州・沖縄',
    key: 'kyushu-okinawa',
    prefectures: ['福岡県', '佐賀県', '長崎県', '大分県', '熊本県', '宮崎県', '鹿児島県', '沖縄県'],
  },
];

@Component({
  selector: 'ag-prefecture-area-select',
  templateUrl: './prefecture-area-select.component.html',
  styleUrls: ['./prefecture-area-select.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PrefectureAreaSelectComponent),
      multi: true,
    },
  ],
})
export class PrefectureAreaSelectComponent implements OnInit, ControlValueAccessor {
  @Input() disabled = false;
  @Output() valueChange = new EventEmitter<string[]>();

  areaGroups = AREA_GROUPS;
  selectedPrefectures: Set<string> = new Set();

  private onChange: (value: string[]) => void = () => {};
  private onTouched: () => void = () => {};

  ngOnInit(): void {}

  // ControlValueAccessor implementation
  writeValue(value: string[] | null): void {
    this.selectedPrefectures = new Set(value || []);
  }

  registerOnChange(fn: (value: string[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  // Check if all prefectures in Japan are selected
  get isAllJapanSelected(): boolean {
    const allPrefectures = this.getAllPrefectures();
    return allPrefectures.every((p) => this.selectedPrefectures.has(p));
  }

  // Check if some (but not all) prefectures are selected
  get isAllJapanIndeterminate(): boolean {
    const allPrefectures = this.getAllPrefectures();
    const selectedCount = allPrefectures.filter((p) => this.selectedPrefectures.has(p)).length;
    return selectedCount > 0 && selectedCount < allPrefectures.length;
  }

  // Check if all prefectures in an area are selected
  isAreaSelected(area: AreaGroup): boolean {
    return area.prefectures.every((p) => this.selectedPrefectures.has(p));
  }

  // Check if some (but not all) prefectures in an area are selected
  isAreaIndeterminate(area: AreaGroup): boolean {
    const selectedCount = area.prefectures.filter((p) => this.selectedPrefectures.has(p)).length;
    return selectedCount > 0 && selectedCount < area.prefectures.length;
  }

  // Check if a prefecture is selected
  isPrefectureSelected(prefecture: string): boolean {
    return this.selectedPrefectures.has(prefecture);
  }

  // Toggle all prefectures (Japan-wide)
  onAllJapanChange(event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    const allPrefectures = this.getAllPrefectures();

    if (checked) {
      for (const p of allPrefectures) {
        this.selectedPrefectures.add(p);
      }
    } else {
      this.selectedPrefectures.clear();
    }

    this.emitChange();
  }

  // Toggle all prefectures in an area
  onAreaChange(area: AreaGroup, event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;

    if (checked) {
      for (const p of area.prefectures) {
        this.selectedPrefectures.add(p);
      }
    } else {
      for (const p of area.prefectures) {
        this.selectedPrefectures.delete(p);
      }
    }

    this.emitChange();
  }

  // Toggle a single prefecture
  onPrefectureChange(prefecture: string, event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;

    if (checked) {
      this.selectedPrefectures.add(prefecture);
    } else {
      this.selectedPrefectures.delete(prefecture);
    }

    this.emitChange();
  }

  private getAllPrefectures(): string[] {
    return this.areaGroups.reduce((acc, area) => acc.concat(area.prefectures), [] as string[]);
  }

  private emitChange(): void {
    const value = Array.from(this.selectedPrefectures);
    this.onChange(value);
    this.onTouched();
    this.valueChange.emit(value);
  }
}
